import { useEffect, useMemo, useState } from "react";
import { Button } from "../components/Button";
import { Chip } from "../components/Chip";
import { MiniBar } from "../components/MiniBar";
import { StatusBadge } from "../components/StatusBadge";
import { Overview } from "./detail/Overview";
import { ServicesConfigurator } from "./detail/ServicesConfigurator";
import { SeatsManager } from "./detail/SeatsManager";
import { TermsManager } from "./detail/TermsManager";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import { fmtDate, fmtMoney, monthsBetween } from "../lib/format";
import type { Route } from "../routing";
import type { SubscriptionDTO } from "@shared/api";

type Tab = "overview" | "services" | "seats" | "terms";

interface Props {
  subId: string;
  onNav: (r: Route) => void;
  onOpenSubscription: (id: string) => void;
}

export function SubscriptionDetail({
  subId,
  onNav,
  onOpenSubscription,
}: Props) {
  const [tab, setTab] = useState<Tab>("overview");
  useEffect(() => setTab("overview"), [subId]);

  const subQ = useFetch(() => api.subscriptions.get(subId), [subId]);
  const allSubsQ = useFetch(() => api.subscriptions.list(), [subId]);
  const usersQ = useFetch(() => api.users.list(), [subId]);
  const servicesQ = useFetch(() => api.services.list(), []);

  const sub: SubscriptionDTO | null = subQ.data;

  const monthlyTotal = useMemo(() => {
    if (!sub || !servicesQ.data) return 0;
    return sub.services.reduce((sum, sid) => {
      const svc = servicesQ.data!.find((c) => c.id === sid);
      return sum + (svc?.monthly ?? 0);
    }, 0);
  }, [sub, servicesQ.data]);

  const assignedUsers = useMemo(() => {
    if (!sub || !usersQ.data) return [];
    return usersQ.data.filter((u) => u.seats.includes(sub.id));
  }, [sub, usersQ.data]);

  const otherSubs = useMemo(() => {
    if (!sub || !allSubsQ.data) return [];
    return allSubsQ.data.filter((s) => s.id !== sub.id);
  }, [sub, allSubsQ.data]);

  async function refresh() {
    await Promise.all([subQ.refresh(), allSubsQ.refresh(), usersQ.refresh()]);
  }

  if (subQ.loading && !sub) {
    return <div className="bbc-screen-loading">Loading subscription…</div>;
  }
  if (subQ.error || !sub) {
    return (
      <div className="bbc-error">
        {subQ.error?.message ?? "Subscription not found."}
        <div style={{ marginTop: 12 }}>
          <Button variant="secondary" onClick={() => onNav("subscriptions")}>
            ← All subscriptions
          </Button>
        </div>
      </div>
    );
  }

  const termMonths = monthsBetween(sub.termStart, sub.termEnd);
  const totalServices = servicesQ.data?.length ?? 0;

  return (
    <div className="bbc-screen">
      <div className="bbc-screen-head bbc-detail-head">
        <div className="bbc-detail-title-wrap">
          <button
            type="button"
            className="bbc-back"
            onClick={() => onNav("subscriptions")}
          >
            ← All subscriptions
          </button>
          <div className="bbc-detail-titlerow">
            <span className="bbc-sub-code mono lg">{sub.code}</span>
            <StatusBadge status={sub.status} />
            <Chip tone="muted">{sub.domain}</Chip>
          </div>
          <h1 className="bbc-h1">{sub.name}</h1>
          <div className="bbc-detail-meta">
            <span>
              <b>{sub.id}</b> · owned by {sub.owner}
            </span>
            <span>Technical contact: {sub.contact}</span>
          </div>
        </div>
        <div className="bbc-screen-actions">
          <Button variant="secondary">Download contract</Button>
          <Button variant="primary">Manage renewal</Button>
        </div>
      </div>

      <div className="bbc-detail-strip">
        <div className="bbc-strip-cell">
          <div className="bbc-strip-label">Seats</div>
          <div className="bbc-strip-value mono">
            {sub.seatsUsed}
            <span className="dim">/{sub.seatsTotal}</span>
          </div>
          <MiniBar
            value={sub.seatsUsed}
            max={Math.max(1, sub.seatsTotal)}
            tone={
              sub.seatsTotal > 0 && sub.seatsUsed / sub.seatsTotal > 0.85
                ? "warn"
                : "default"
            }
          />
        </div>
        <div className="bbc-strip-cell">
          <div className="bbc-strip-label">Term</div>
          <div className="bbc-strip-value mono">
            {termMonths} <span className="dim">months</span>
          </div>
          <div className="bbc-strip-sub">
            {fmtDate(sub.termStart)} → {fmtDate(sub.termEnd)}
          </div>
        </div>
        <div className="bbc-strip-cell">
          <div className="bbc-strip-label">Connected services</div>
          <div className="bbc-strip-value mono">
            {sub.services.length}{" "}
            <span className="dim">/ {totalServices || sub.services.length}</span>
          </div>
          <div className="bbc-strip-sub">
            {fmtMoney(monthlyTotal)} / mo committed
          </div>
        </div>
        <div className="bbc-strip-cell">
          <div className="bbc-strip-label">Deployments</div>
          <div className="bbc-strip-value mono">{sub.deployments}</div>
          <div className="bbc-strip-sub">{sub.monthlyCalls} calls / mo</div>
        </div>
        <div className="bbc-strip-cell">
          <div className="bbc-strip-label">IP scope</div>
          <div className="bbc-strip-value bbc-strip-value-sm">
            {sub.ipScope}
          </div>
          <div className="bbc-strip-sub">
            {sub.autoRenew ? "Auto-renew on" : "Manual renewal"}
          </div>
        </div>
      </div>

      <div className="bbc-tabs">
        {(
          [
            { id: "overview", label: "Overview" },
            {
              id: "services",
              label: `Connected services · ${sub.services.length}`,
            },
            {
              id: "seats",
              label: `Seats & users · ${assignedUsers.length}`,
            },
            { id: "terms", label: "Term & IP rights" },
          ] as { id: Tab; label: string }[]
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            className={tab === t.id ? "is-on" : ""}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <Overview
          sub={sub}
          services={servicesQ.data ?? []}
          monthlyTotal={monthlyTotal}
          assignedUsers={assignedUsers}
          otherSubs={otherSubs}
          onOpenSubscription={onOpenSubscription}
          setTab={(t) => setTab(t)}
        />
      )}
      {tab === "services" && (
        <ServicesConfigurator
          sub={sub}
          services={servicesQ.data ?? []}
          termMonths={termMonths}
          onSaved={refresh}
        />
      )}
      {tab === "seats" && (
        <SeatsManager
          sub={sub}
          assignedUsers={assignedUsers}
          onChanged={refresh}
        />
      )}
      {tab === "terms" && <TermsManager sub={sub} onSaved={refresh} />}
    </div>
  );
}
